/**
 * Nature picture-tier vignettes (AUTHORING.md v3): one painted background per picture (public/vignettes/nature/<bg>.webp,
 * prompts docs/vignette-prompts.py, CREDITS.md) + the picture's posed object from core (`content/worlds/nature/pictures.ts`).
 * `elements` = every part the painting shows (each one is drawn + coloured in the picture lesson); `overlay` = taught
 * props the painting lacks (drawn over it).
 */
import type { VignetteSpec, WorldVignettes } from '../../vignette/types';

const v = (id: string, elements: string[], overlay?: string[]): VignetteSpec => ({ id: `${id}-picture`, bg: id, elements, ...(overlay ? { overlay } : {}) });

const specs: VignetteSpec[] = [
  /* L1 · spring */
  v('nature-tulip', ['sky', 'ground', 'fence', 'flowers']),
  v('nature-mushroom', ['sky', 'ground', 'trees', 'ferns']),
  v('nature-remix-mushroom-striped', ['sky', 'ground', 'river', 'stones', 'tulips']),
  v('nature-remix-mushroom-trio', ['sky', 'ground', 'log', 'bushes', 'flowers']),
  v('nature-remix-mushroom-umbrella', ['sky', 'clouds', 'ground', 'reeds']),
  /* L2 · summer */
  v('nature-sunflower', ['sky', 'sun', 'fields', 'fence']),
  v('nature-cactus', ['sky', 'sun', 'dunes', 'ground', 'rocks']),
  v('nature-beach-sunset', ['sky', 'sea', 'ground', 'castle', 'shells']),
  v('nature-remix-cactus-bloom', ['sky', 'cliffs', 'ground', 'stones']),
  v('nature-remix-cactus-hat', ['sky', 'clouds', 'sea', 'ground', 'towel', 'bucket']),
  v('nature-remix-cactus-mug', ['sky', 'ground', 'window', 'plant']),
  /* L3 · autumn */
  v('nature-oak-tree', ['sky', 'clouds', 'hills', 'ground', 'path']),
  v('nature-acorn-friends', ['sky', 'ground', 'trees', 'mushrooms']),
  v('nature-apple-basket', ['sky', 'ground', 'trees', 'ladder']),
  v('nature-remix-oak-swing', ['sky', 'fields', 'roofs']),
  v('nature-remix-oak-birdhouse', ['sky', 'ground', 'pond', 'reeds']),
  v('nature-remix-oak-windy', ['sky', 'clouds', 'hills', 'ground', 'tallGrass']),
  v('nature-remix-acorns-beanie', ['sky', 'ground', 'pines', 'stones']),
  v('nature-remix-acorns-leaf-boat', ['sky', 'trees', 'lake', 'ground', 'reeds']),
  v('nature-remix-acorns-nest', ['sky', 'moon', 'stars', 'ground', 'bushes']),
  /* L4 · winter */
  v('nature-pine-tree', ['sky', 'mountains', 'ground', 'pines']),
  v('nature-snowy-cabin', ['sky', 'mountains', 'ground', 'pines']),
  v('nature-mountain-lake', ['sky', 'ground', 'curtains', 'books']),
  v('nature-remix-pine-lights', ['sky', 'stars', 'roofs', 'ground', 'lamps']),
  v('nature-remix-pine-birds', ['sky', 'ground', 'fence', 'bushes']),
  v('nature-remix-pine-blizzard', ['sky', 'mountains', 'ground', 'rocks']),
  v('nature-remix-cabin-night', ['sky', 'moon', 'mountains', 'ground', 'pines']),
  v('nature-remix-cabin-snowman', ['sky', 'clouds', 'ground', 'pines']),
  v('nature-remix-cabin-sled', ['sky', 'hills', 'ground', 'pines']),
  /* playground-only */
  v('nature-rainbow-cloud', ['sky', 'hills', 'ground', 'flowers']),
];

export const NATURE_VIGNETTES: WorldVignettes = { world: 'nature', specs };
